import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Avatar, Chip, Paper, Grid,
  Divider, IconButton, Tooltip, alpha,
  List, ListItem, ListItemIcon, ListItemText,
  Table, TableBody, TableCell, TableRow,
  Stack, Rating
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Event as EventIcon,
  DirectionsCar,
  TwoWheeler,
  LocalShipping,
  Description as DescriptionIcon,
  FileCopy as FileCopyIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Verified as VerifiedIcon,
  AirportShuttle, CommuteOutlined, DescriptionOutlined,
  TrendingUp, Gavel, WbSunny, LocalFireDepartment,
  SwapHoriz, DirectionsBus
} from "@mui/icons-material";
import { BASE_URL } from "../services/customerApi";

const getVehicleIcon = (type) => {
  switch ((type || "two_wheeler")?.toLowerCase()) {
    case "two_wheeler": return <TwoWheeler fontSize="small" />;
    case "private_car": return <DirectionsCar fontSize="small" />;
    case "passengers_vehicle": return <AirportShuttle fontSize="small" />;
    case "goods_vehicle": return <LocalShipping fontSize="small" />;
    case "miscellaneous_vehicle": return <CommuteOutlined fontSize="small" />;
    case "non_motor": return <DescriptionOutlined fontSize="small" />;
    case "idv_increase": return <TrendingUp fontSize="small" />;
    case "hp_endorsement": return <Gavel fontSize="small" />;
    case "solar_policy": return <WbSunny fontSize="small" />;
    case "fire_policy": return <LocalFireDepartment fontSize="small" />;
    case "name_transfer": return <SwapHoriz fontSize="small" />;
    case "school_bus": return <DirectionsBus fontSize="small" />;
    default: return <DirectionsCar fontSize="small" />;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "Expired": return "error";
    case "Expiring Soon": return "warning";
    case "Active": return "success";
    default: return "default";
  }
};

const getStatusText = (endDate) => {
  if (!endDate) return "No Date";
  const end = new Date(endDate);
  const today = new Date();
  const diffTime = end - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "Expired";
  if (diffDays <= 15) return "Expiring Soon";
  return "Active";
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " bytes";
  else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  else return (bytes / 1048576).toFixed(1) + " MB";
};

export default function CustomerDetailsView({
  open,
  onClose,
  user,
  getUserStatus,
  handleEditUser,
  handleFilePreview,
  loggedUserType
}) {
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  if (!user) return null;

  const copyToClipboard = (text, message) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({
        open: true,
        message: message || "Copied to clipboard!",
        severity: "success"
      });
      setTimeout(() => setSnackbar({ ...snackbar, open: false }), 3000);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not Set";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const handleDownloadFile = (file) => {
    let url = file.url;
    if (url && url.startsWith("/api")) {
      url = `${BASE_URL}${url}`;
    }

    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "1100px",
            width: "100%",
            maxHeight: { xs: "100vh", md: "95vh" },
            margin: { xs: 0, sm: "20px" },
            borderRadius: { xs: 0, sm: "12px" },
            background: "#0a0a0a",
            color: "#ffffff"
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#111111",
          py: { xs: 1.5, md: 2.5 },
          px: { xs: 2, md: 3 },
          borderBottom: "1px solid rgba(253, 184, 19, 0.2)"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <PersonIcon sx={{ color: "#FDB813", fontSize: { xs: "1.2rem", md: "1.5rem" } }} />
            <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: "1rem", md: "1.25rem" }, fontFamily: "'Outfit', sans-serif" }}>
              Customer Details
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
          {/* Horizontal Personal Information Section */}
          <Paper sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            mb: 3,
            background: "#111111",
            border: "1px solid rgba(253, 184, 19, 0.1)",
            boxShadow: "0 10px 40px rgba(0,0,0,0.5)"
          }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              gutterBottom
              sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1, color: "#FDB813", fontFamily: "'Outfit', sans-serif" }}
            >
              <PersonIcon fontSize="small" />
              Personal Information
            </Typography>

            <Grid container spacing={3} alignItems="center">
              {/* Avatar and Name Section */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center"
                }}>
                  <Avatar sx={{
                    bgcolor: "rgba(253, 184, 19, 0.1)",
                    color: "#FDB813",
                    width: { xs: 80, md: 100 },
                    height: { xs: 80, md: 100 },
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    mb: 2,
                    border: "2px solid rgba(253, 184, 19, 0.2)",
                    fontFamily: "'Outfit', sans-serif",
                    fontWeight: 800
                  }}>
                    {(user.name || "?").charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="h6" fontWeight={800} sx={{ mb: 1, fontSize: { xs: "1.2rem", md: "1.4rem" }, color: "#ffffff", fontFamily: "'Outfit', sans-serif" }}>
                    {user.name || "--"}
                  </Typography>
                   <Chip
                    label={getUserStatus ? getUserStatus(user) : getStatusText(user.endDate)}
                    color={getStatusColor(getUserStatus ? getUserStatus(user) : getStatusText(user.endDate))}
                    size="medium"
                    variant="outlined"
                    sx={{
                      fontWeight: 700,
                      px: 2,
                      fontFamily: "'Poppins', sans-serif",
                    }}
                  />

                  {/* Rating & Verification Status */}
                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    <Rating 
                      value={user.rating || 0} 
                      readOnly 
                      sx={{ 
                        "& .MuiRating-iconFilled": { color: "#FDB813" },
                        "& .MuiRating-iconEmpty": { color: "rgba(255,255,255,0.1)" },
                        fontSize: "1.5rem"
                      }} 
                    />
                    
                    {user.verification === "Yes" ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#4caf50" }}>
                        <VerifiedIcon sx={{ fontSize: "1rem" }} />
                        <Typography variant="caption" fontWeight={700} sx={{ fontFamily: "'Poppins', sans-serif" }}>
                          VERIFIED {user.verifiedBy && `BY ${user.verifiedBy.toUpperCase()}`}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.3)", fontWeight: 600, fontFamily: "'Poppins', sans-serif" }}>
                        NOT VERIFIED
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              {/* Contact Information - Horizontal Layout */}
              <Grid size={{ xs: 12, md: 9 }}>
                <Grid container spacing={2}>
                  {/* Phone Section */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper sx={{
                      p: 2.5,
                      borderRadius: 2,
                      minHeight: "100px",
                      background: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, width: "100%" }}>
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          backgroundColor: "rgba(253, 184, 19, 0.1)",
                          color: "#FDB813",
                          flexShrink: 0,
                          border: "1px solid rgba(253, 184, 19, 0.2)"
                        }}>
                          <PhoneIcon fontSize="sm" />
                        </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                mb: 0.5,
                                display: "block",
                                fontWeight: 700,
                                fontSize: "0.75rem",
                                textTransform: "uppercase",
                                letterSpacing: "1px",
                                color: "rgba(255, 255, 255, 0.5)",
                                fontFamily: "'Poppins', sans-serif"
                              }}
                            >
                              Phone Number
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography fontWeight={700} fontSize="1.1rem" sx={{ color: "#ffffff", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.5px" }}>
                                {user.phone || "--"}
                              </Typography>
                              <Tooltip title="Copy phone number">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(user.phone, "Phone number copied!")}
                                  sx={{
                                    p: 0.5,
                                    color: "rgba(255, 255, 255, 0.4)",
                                    "&:hover": { color: "#FDB813", backgroundColor: "rgba(253, 184, 19, 0.1)" }
                                  }}
                                >
                                  <FileCopyIcon sx={{ fontSize: "1rem" }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Email Section */}
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper sx={{
                      p: 2.5,
                      borderRadius: 2,
                      minHeight: "100px",
                      background: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, width: "100%" }}>
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          backgroundColor: "rgba(253, 184, 19, 0.1)",
                          color: "#FDB813",
                          flexShrink: 0,
                          border: "1px solid rgba(253, 184, 19, 0.2)"
                        }}>
                          <EmailIcon fontSize="sm" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              mb: 0.5,
                              display: "block",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              color: "rgba(255, 255, 255, 0.5)",
                              fontFamily: "'Poppins', sans-serif"
                            }}
                          >
                            Email Address
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                            <Typography
                              fontWeight={700}
                              fontSize="1rem"
                              sx={{
                                color: "#ffffff",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontFamily: "'Outfit', sans-serif"
                              }}
                            >
                              {user.email || "--"}
                            </Typography>
                            {user.email && (
                              <Tooltip title="Copy email">
                                <IconButton
                                  size="small"
                                  onClick={() => copyToClipboard(user.email, "Email copied!")}
                                  sx={{ color: "rgba(255, 255, 255, 0.5)", "&:hover": { color: "#FDB813", backgroundColor: "rgba(253, 184, 19, 0.1)" } }}
                                >
                                  <FileCopyIcon sx={{ fontSize: "1.1rem" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Location Section */}
                  <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                    <Paper sx={{
                      p: 2.5,
                      borderRadius: 2,
                      minHeight: "100px",
                      background: "rgba(255, 255, 255, 0.03)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center"
                    }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, width: "100%" }}>
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          backgroundColor: "rgba(253, 184, 19, 0.1)",
                          color: "#FDB813",
                          flexShrink: 0,
                          border: "1px solid rgba(253, 184, 19, 0.2)"
                        }}>
                          <LocationOnIcon fontSize="sm" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              mb: 0.5,
                              display: "block",
                              fontWeight: 700,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              color: "rgba(255, 255, 255, 0.5)",
                              fontFamily: "'Poppins', sans-serif"
                            }}
                          >
                            Location
                          </Typography>
                          <Typography fontWeight={700} fontSize="1rem" sx={{ color: "#ffffff", fontFamily: "'Outfit', sans-serif" }}>
                            {user.location || "--"}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={3}>
            {/* Left Column - Vehicle Details & Insurance */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                {/* Vehicle Details */}
                <Paper sx={{
                  p: 3,
                  borderRadius: 2,
                  background: "#111111",
                  border: "1px solid rgba(253, 184, 19, 0.1)",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.5)"
                }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    gutterBottom
                    sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, color: "#FDB813", fontFamily: "'Outfit', sans-serif" }}
                  >
                    <DirectionsCar fontSize="small" />
                    Vehicle Details
                  </Typography>

                  {user.vehicles?.filter(v => v.number?.trim()).length > 0 ? (
                    <Grid container spacing={2}>
                      {user.vehicles
                        .filter(v => v.number?.trim())
                        .map((vehicle, idx) => (
                          <Grid size={{ xs: 12 }} key={idx}>
                            <Paper
                              sx={{
                                p: 2.5,
                                borderRadius: 2,
                                backgroundColor: "rgba(253, 184, 19, 0.05)",
                                height: "100%",
                                border: "1px solid rgba(253, 184, 19, 0.2)",
                                transition: "all 0.2s",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
                                }
                              }}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Box sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 48,
                                  height: 48,
                                  borderRadius: "12px",
                                  backgroundColor: "#FDB813",
                                  color: "#111111",
                                  flexShrink: 0
                                }}>
                                  {getVehicleIcon(vehicle.type)}
                                </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={800}
                                        sx={{
                                          color: "#FDB813",
                                          fontSize: "1.05rem",
                                          fontFamily: "'Outfit', sans-serif"
                                        }}
                                      >
                                        {(vehicle.type || "car").toUpperCase()}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontFamily: "monospace",
                                          fontSize: "0.95rem",
                                          color: "#ffffff",
                                          fontWeight: 600,
                                          letterSpacing: "0.5px"
                                        }}
                                      >
                                        {vehicle.number}
                                      </Typography>
                                    </Box>
                                  {vehicle.endDate && (
                                    <Box sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                      gap: 1.5,
                                      mt: 1,
                                      pt: 1,
                                      borderTop: "1px solid rgba(255,255,255,0.05)"
                                    }}>
                                      {vehicle.scheduleDate && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                          <EventIcon sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.4)" }} />
                                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", fontWeight: 500 }}>
                                            Schedule: {formatDate(vehicle.scheduleDate)}
                                          </Typography>
                                        </Box>
                                      )}
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <EventIcon sx={{ fontSize: "0.9rem", color: "rgba(255,255,255,0.4)" }} />
                                        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", fontWeight: 500 }}>
                                          Expires: {formatDate(vehicle.endDate)}
                                        </Typography>
                                      </Box>
                                      <Chip 
                                        label={getStatusText(vehicle.endDate)} 
                                        color={getStatusColor(getStatusText(vehicle.endDate))} 
                                        size="small" 
                                        variant="outlined"
                                        sx={{ 
                                          height: 22, 
                                          fontSize: '0.7rem', 
                                          fontWeight: 800, 
                                          borderRadius: "4px",
                                          px: 1,
                                          borderColor: alpha(getStatusColor(getStatusText(vehicle.endDate)) === "error" ? "#f44336" : "#FDB813", 0.5)
                                        }} 
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                    </Grid>
                  ) : (
                    <Box sx={{
                      textAlign: "center",
                      py: 4,
                      border: "1px dashed rgba(253, 184, 19, 0.3)",
                      borderRadius: 2,
                      backgroundColor: "rgba(253, 184, 19, 0.02)"
                    }}>
                      <DirectionsCar sx={{
                        fontSize: 48,
                        color: "rgba(255, 255, 255, 0.3)",
                        mb: 2
                      }} />
                      <Typography sx={{ color: "rgba(255, 255, 255, 0.5)", fontFamily: "'Poppins', sans-serif" }}>
                        No vehicles registered
                      </Typography>
                    </Box>
                  )}
                </Paper>

              </Stack>
            </Grid>

            {/* Right Column - Documents & Additional Info */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={3}>
                {/* Documents Summary */}
                <Paper sx={{
                  p: 3,
                  borderRadius: 2,
                  height: "100%",
                  background: "#111111",
                  border: "1px solid rgba(253, 184, 19, 0.1)",
                  boxShadow: "0 10px 40px rgba(0,0,0,0.5)"
                }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    gutterBottom
                    sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, color: "#FDB813", fontFamily: "'Outfit', sans-serif" }}
                  >
                    <DescriptionIcon fontSize="small" />
                    Documents Summary ({user.documents?.length || 0})
                  </Typography>

                  {user.documents?.length > 0 ? (
                    <Box>
                      <List disablePadding>
                        {user.documents.slice(0, 4).map((file, idx) => (
                          <ListItem
                            key={idx}
                            sx={{
                              px: 0,
                              py: 2,
                              borderBottom: idx < Math.min(user.documents.length, 4) - 1 ? "1px solid #eee" : "none",
                              cursor: "pointer",
                              "&:hover": {
                                backgroundColor: "action.hover",
                                borderRadius: "8px"
                              }
                            }}
                            onClick={() => handleFilePreview && handleFilePreview(file)}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 36,
                                height: 36,
                                borderRadius: "8px",
                                backgroundColor: alpha("#667eea", 0.1),
                                color: "#667eea"
                              }}>
                                <DescriptionIcon fontSize="small" />
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  fontWeight={600}
                                  sx={{ fontSize: "0.9rem" }}
                                >
                                  {file.name}
                                </Typography>
                              }
                              secondary={
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.8rem" }}
                                >
                                  {file.type?.split('/')[1]?.toUpperCase()} • {formatFileSize(file.size)}
                                </Typography>
                              }
                            />
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadFile(file);
                                  }}
                                  sx={{ color: "text.secondary" }}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Preview">
                                <IconButton
                                  size="small"
                                  onClick={() => handleFilePreview && handleFilePreview(file)}
                                  sx={{ color: "text.secondary" }}
                                >
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </ListItem>
                        ))}
                      </List>

                      {user.documents?.length > 4 && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mt: 2,
                            textAlign: "center",
                            fontSize: "0.8rem"
                          }}
                        >
                          +{user.documents.length - 4} more documents
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box sx={{
                      textAlign: "center",
                      py: 4,
                      border: "1px dashed rgba(253, 184, 19, 0.3)",
                      borderRadius: 2,
                      backgroundColor: "rgba(253, 184, 19, 0.02)"
                    }}>
                      <DescriptionIcon sx={{
                        fontSize: 48,
                        color: "rgba(255, 255, 255, 0.3)",
                        mb: 2
                      }} />
                      <Typography sx={{ color: "rgba(255, 255, 255, 0.5)", fontFamily: "'Poppins', sans-serif" }}>
                        No documents uploaded
                      </Typography>
                    </Box>
                  )}
                </Paper>

              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid rgba(253, 184, 19, 0.2)",
          gap: 2,
          backgroundColor: "#111111"
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              px: 3,
              textTransform: "none",
              fontWeight: 600,
              color: "#ffffff",
              borderColor: "rgba(255, 255, 255, 0.3)",
              fontFamily: "'Outfit', sans-serif"
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              if (handleEditUser) {
                handleEditUser(user);
                onClose();
              }
            }}
            sx={{
              px: 4,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 800,
              backgroundColor: "#FDB813",
              color: "#111111",
              fontFamily: "'Outfit', sans-serif",
              "&:hover": {
                backgroundColor: "#e5a100"
              }
            }}
          >
            Edit Customer
          </Button>
        </DialogActions>
      </Dialog >
    </>
  );
}